import {AfterViewInit, Component, ElementRef, OnInit, Signal, ViewChild} from '@angular/core';
import {RmaService} from "../../api/rma.service";
import {Rma, RmaStatusType} from "../../api/model/rma";
import {MatDialog} from "@angular/material/dialog";
import {Router} from "@angular/router";
import {CreateRmaDialogComponent} from "../create-rma-dialog/create-rma-dialog.component";
import {MatTableDataSource} from "@angular/material/table";
import {fromEvent} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {CheckBoxOption} from "../../equipment/check-box-filter/check-box-filter.component";
import _ from "lodash";
import {AlertService} from "../../api/alert.service";
import {AlertDetailed} from "../../api/model/alert";

@Component({
  selector: 'app-rma-list',
  templateUrl: './rma-list.component.html',
  styleUrls: ['./rma-list.component.sass']
})
export class RmaListComponent implements OnInit, AfterViewInit {
  rmas: Rma[] = [];
  dataSource = new MatTableDataSource<Rma>();
  alerts: Signal<AlertDetailed[]> = this.alertService.rmaAlerts;

  defaultColumns: Column[] = [
    {key: "rmaNumber", label: "RMA Number", visible: true},
    {key: "loaner", label: "Loaner", visible: true},
    {key: "warranty", label: "Warranty", visible: true},
    {key: "created", label: "Created", visible: true},
    {key: "lastStatus", label: "Last Status", visible: true},
    {key: "clientName", label: "Client Name", visible: true},
    {key: "manufacturer", label: "Manufacturer", visible: true},
    {key: "sku", label: "SKU", visible: true},
    {key: "model", label: "Model", visible: true},
    {key: "description", label: "Problem Description", visible: true},
    {key: "serialNumber", label: "Serial Number", visible: true},
    {key: "timeSpent", label: "Hours Spent", visible: true},
  ];
  columns: Column[] = _.cloneDeep(this.defaultColumns);

  filterValue: string = "";
  filterStatuses: RmaStatusType[] = [];
  filterLoading: boolean = true;
  order: string = "rmaNumber-desc";
  statuses = Object.values(RmaStatusType);

  loanerFilterOptions: CheckBoxOption[] = [
    {display: "Has loaners", value: "HAS"},
    {display: "Doesn't have loaners", value: "DOESNT_HAVE"},
  ];
  loanerFilterSelected: string[] = [];

  warrantyFilterOptions: CheckBoxOption[] = [
    {display: "Is under warranty", value: "HAS"},
    {display: "Is not under warranty", value: "DOESNT_HAVE"},
  ];
  warrantyFilterSelected: string[] = [];

  @ViewChild('filter') filterField!: ElementRef;

  constructor(
    public dialog: MatDialog,
    private rmaService: RmaService,
    private alertService: AlertService,
    private router: Router,
  ) {
    this.order = localStorage.getItem('rmaListOrder') || 'rmaNumber-desc';
    const savedStatuses = localStorage.getItem('rmaStatusFilter');
    this.filterStatuses = savedStatuses ? savedStatuses.split(',').filter(s => s) as RmaStatusType[] : [];

    const persistedColumns = JSON.parse(localStorage.getItem('rmaListColumns') || JSON.stringify(this.defaultColumns)) as Column[];
    this.columns.forEach(c => {
      const persistedVis = persistedColumns.find(pc => pc && pc.key == c.key);
      if (persistedVis !== undefined) c.visible = persistedVis.visible;
    });
  }

  get displayedColumns(): string[] {
    return this.columns.filter(c => c.visible).map(c => c.key);
  }

  toggleColumnVisibility(c: Column) {
    c.visible = !c.visible;
    this.persistColumns();
  }

  resetColumnVisibility() {
    this.columns = _.cloneDeep(this.defaultColumns);
    this.persistColumns();
  }

  ngOnInit() {
    this.loadRmas();
  }

  ngAfterViewInit() {
    const keyUpEvent = fromEvent(this.filterField.nativeElement, 'keyup');
    keyUpEvent.subscribe(() => {
      this.filterLoading = true;
    });
    keyUpEvent.pipe(debounceTime(500)).subscribe(() => {
      this.loadRmas();
    });
  }

  loadRmas() {
    this.filterLoading = true;
    this.rmaService.list(this.filterValue || undefined).subscribe(rmas => {
      this.rmas = rmas;
      this.applyFiltersAndSort();
      this.filterLoading = false;
    });
  }

  setFilter(filter: string, $event: MouseEvent) {
    this.filterValue = filter;
    this.loadRmas();
    $event.stopImmediatePropagation();
  }

  clearFilter() {
    this.filterValue = '';
    this.loadRmas();
  }

  isFilterEmpty(): boolean {
    return this.filterValue.length === 0;
  }

  onStatusFilterChange() {
    localStorage.setItem('rmaStatusFilter', this.filterStatuses.join(','));
    this.applyFiltersAndSort();
  }

  onLoanerFilterChange(selected: string[]) {
    this.loanerFilterSelected = selected;
    this.applyFiltersAndSort();
  }

  onWarrantyFilterChange(selected: string[]) {
    this.warrantyFilterSelected = selected;
    this.applyFiltersAndSort();
  }

  onOrderChange() {
    localStorage.setItem('rmaListOrder', this.order);
    this.applyFiltersAndSort();
  }

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(CreateRmaDialogComponent, {
      data: {},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const rma = result as Rma;
        this.router.navigate(["/service", rma.rmaNumber]);
      }
    });
  }

  jumpTo(rma: Rma) {
    this.router.navigate(["/service", rma.rmaNumber]);
  }

  loanedItemTooltip(rma: Rma): string {
    return `Substitute${rma.loanedItems.length > 1 ? 's were' : ' was'} loaned`;
  }

  alertsFor(rma: Rma, alerts: AlertDetailed[]): AlertDetailed[] | undefined {
    return rmaAlertsFor(rma, alerts);
  }

  alertsTooltip(rma: Rma, alerts: AlertDetailed[]): string {
    return rmaAlertsTooltip(rma, alerts);
  }

  private persistColumns() {
    localStorage.setItem('rmaListColumns', JSON.stringify(this.columns));
  }

  private applyFiltersAndSort() {
    let result = [...this.rmas];
    if (this.filterStatuses.length > 0) {
      result = result.filter(r => {
        const lastStatus = r.statuses.at(-1)?.type;
        return lastStatus !== undefined && this.filterStatuses.includes(lastStatus);
      });
    }
    if (this.loanerFilterSelected.length > 0 && this.loanerFilterSelected.length < 2) {
      const hasLoaners = this.loanerFilterSelected.includes("HAS");
      result = result.filter(r => hasLoaners ? r.loanedItems.length > 0 : r.loanedItems.length === 0);
    }
    if (this.warrantyFilterSelected.length > 0 && this.warrantyFilterSelected.length < 2) {
      const hasWarranty = this.warrantyFilterSelected.includes("HAS");
      result = result.filter(r => r.isWarranty === hasWarranty);
    }
    const [field, dir] = this.order.split('-');
    result.sort((a, b) => {
      let cmp = 0;
      if (field === 'rmaNumber') cmp = a.rmaNumber.localeCompare(b.rmaNumber);
      else if (field === 'created') cmp = new Date(a.created.at).getTime() - new Date(b.created.at).getTime();
      else if (field === 'timeSpent') cmp = (a.totalTimeSpentInHours ?? 0) - (b.totalTimeSpentInHours ?? 0);
      return dir === 'desc' ? -cmp : cmp;
    });
    this.dataSource.data = result;
  }
}

interface Column {
  key: string;
  label: string;
  visible: boolean;
}

export function rmaAlertsFor(rma: Rma, alerts: AlertDetailed[]): AlertDetailed[] | undefined {
  const filtered = alerts.filter(a => a.belongsToId === rma.id);
  return filtered.length > 0 ? filtered : undefined;
}

export function rmaAlertsTooltip(rma: Rma, alerts: AlertDetailed[]): string {
  return alerts.map(a => a.message).join('<br>\n');
}
